import type { StickyModelContentsOperation } from '@view-model/adapter';
import { toast } from 'react-hot-toast';
import { t } from 'i18next';
import type { ApplicationClipboardPayload } from '@view-model/application/clipboard';
import { isFileClipboardPayload, isImageClipboardPayload } from '@view-model/application/clipboard';
import type { FileUploadPasteContext } from './IStickyModelContentsViewHandlers';
import { ReadonlyHandlers } from './ReadonlyHandlers';
import type { Point, Size } from '@view-model/models/common/basic';
import type { PositionDelta } from '@view-model/models/common/types/ui';
import type { FileContainerId } from 'balus-common';

export class WritableHandlers extends ReadonlyHandlers {
  constructor(
    protected readonly operation: StickyModelContentsOperation,
    private readonly onDeleteView: () => void
  ) {
    super(operation);
  }

  handleArrowKeys(dx: number, dy: number): void {
    this.operation.moveSelectedElements(dx, dy);
  }

  handleDeleteSelectedElements(): void {
    if (this.operation.hasSelectedElements()) {
      this.operation.deleteSelectedElements().then((deleted) => {
        if (deleted) toast(t('canvas.stickyModel.handler.deleted'));
      });
    } else {
      // 選択している要素がない場合はViewの削除としてコールバックを呼ぶ
      this.onDeleteView();
    }
  }

  handleDeleteFileContainer(id: FileContainerId): void {
    this.operation.deleteFileContainer(id).then((deleted) => {
      if (deleted) toast(t('canvas.stickyModel.handler.deleted'));
    });
  }

  handleResizeFileContainer(id: FileContainerId, positionDelta: PositionDelta, size: Size): void {
    this.operation.resizeFileContainer(id, positionDelta, size);
  }

  async handleCutSelectedElements(): Promise<void> {
    const { operation } = this;

    if (!operation.hasSelectedElements()) return;

    if (await operation.copySelectedElements()) {
      operation.deleteSelectedElements().then((deleted) => {
        if (deleted) toast.success(t('canvas.stickyModel.handler.cutSuccess'));
      });
    } else {
      toast.error(t('canvas.stickyModel.handler.cutFailed'));
    }
  }

  async handlePasteClipboardPayload(
    payload: ApplicationClipboardPayload,
    position: Point,
    fileUpload: FileUploadPasteContext
  ): Promise<void> {
    const isFilePayload = isFileClipboardPayload(payload) || isImageClipboardPayload(payload);
    if (isFilePayload && fileUpload.deniedMessage) {
      toast.error(fileUpload.deniedMessage);
      return;
    }

    const toastId = 'sticky-model-contents-pasting';
    toast(t('canvas.stickyModel.handler.pasting'), { id: toastId, duration: Infinity });

    const err = await this.operation.pasteClipboardPayload(payload, position, fileUpload.onFileContainerCreated);
    toast.dismiss(toastId);

    if (err) {
      toast.error(err);
    }
  }

  handleDuplicateSelectedElements(position: Point): void {
    this.operation.duplicateSelectedElements(position).then((err) => {
      if (err) toast.error(err);
    });
  }
}
